<template>
  <footer
    :class="[
      'mx-5 mt-25 border-t border-gray border-solid pt-7.5 pb-5 md:mt-32.75 lg:mx-10 lg:pb-9.5 xl:mt-42.5 2xl:mt-47.5',
      isDarkTheme ? 'text-white' : 'text-black',
    ]"
  >
    <div class="flex flex-col gap-18 md:gap-23.5 lg:gap-24.75 xl:gap-21.75">
      <NuxtLink
        :to="localePath('index')"
        class="mx-auto max-w-62.5 text-center font-header text-1.5xl leading-[118%] md:max-w-none md:text-2.5xl md:leading-[107%] xl:text-4.5xl xl:leading-[105%]"
      >
        <ClientOnly>{{ t('footer.title') }}</ClientOnly>
      </NuxtLink>

      <div class="flex flex-col gap-20 text-sm leading-[142.3%] xl:gap-12.5">
        <div
          class="flex w-max flex-col gap-1.25 ease-out [&_a]:underline [&_a]:underline-offset-2 [&_a]:transition-colors [&_a]:duration-100 [&_a]:hover:text-gray-medium"
          v-html="formattedFooterContent"
          @click="handleFooterClick"
        />

        <div
          class="flex flex-col-reverse justify-between gap-10 lg:flex-row lg:items-center lg:gap-0"
        >
          <div class="flex w-full max-w-84 flex-col xl:max-w-[27.28%]">
            <form
              class="group transform-colors flex w-full items-center justify-between border border-black py-2.25 pr-1.5 pl-2.5 duration-100 ease-out hover:border-gray-darker"
              :class="{
                'pointer-events-none opacity-50': isLoading,
                'pointer-events-none': successMessage,
              }"
              @submit.prevent="onSubmitNewsletter"
              novalidate
            >
              <Transition name="fade" mode="out-in">
                <div
                  v-if="successMessage"
                  key="success"
                  class="flex w-full items-center justify-between"
                >
                  <span
                    class="transform-colors w-full truncate bg-transparent duration-100 ease-out outline-none group-hover:text-gray-darker"
                  >
                    {{ successMessage }}
                  </span>
                  <div
                    class="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-full bg-transparent"
                  >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>

                <div
                  v-else
                  key="input"
                  class="flex w-full items-center justify-between"
                >
                  <input
                    type="email"
                    :placeholder="t('footer.newsletter')"
                    class="transform-colors w-full bg-transparent text-black caret-black duration-100 ease-out outline-none placeholder:text-black group-hover:placeholder:text-gray-darker focus:placeholder:text-gray-400"
                    v-model="newsletterEmail"
                    :disabled="isLoading"
                  />
                  <button
                    type="submit"
                    class="transform-colors flex h-6.5 w-6.5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent transition-opacity group-hover:text-gray-darker hover:opacity-80"
                    :aria-label="t('common.submit')"
                    :disabled="isLoading"
                  >
                    <ArrowIcon class="h-3.5 w-3.5" />
                  </button>
                </div>
              </Transition>
            </form>

            <span v-if="errorMessage" class="mt-2 text-xs text-red-600">
              {{ errorMessage }}
            </span>
          </div>

          <Breadcrumbs />
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { z } from 'zod'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { mutate } = useGqlMutation()
const theme = useTheme()
const isDarkTheme = computed(() => theme.value === 'dark')

const { data } = await usePersistedFetch('Footer')

const decodeHtmlEntities = (str: string): string => {
  if (!str) return ''
  const decoded = str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')

  return decoded
    .replace(/<p>\s*<p>/gi, '<p>')
    .replace(/<\/p>\s*<\/p>/gi, '</p>')
}

const formattedFooterContent = computed(() => {
  const raw = data.value?.footer.content?.[locale.value]
  if (!raw) return ''
  return decodeHtmlEntities(raw)
})

const handleFooterClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const cookieLink = target.closest('.cky-banner-element')

  if (cookieLink) {
    e.preventDefault()
  }
}

const newsletterSchema = z.object({
  email: z
    .string()
    .email(t('errors.invalid.email'))
    .min(1, t('errors.required.email')),
})

const newsletterEmail = ref('')
const errorMessage = ref('')
const successMessage = ref('')
const isLoading = ref(false)

let timerId: ReturnType<typeof setTimeout> | null = null

const clearTimer = () => {
  if (timerId) {
    clearTimeout(timerId)
    timerId = null
  }
}

const onSubmitNewsletter = async () => {
  clearTimer()
  errorMessage.value = ''
  successMessage.value = ''

  const result = newsletterSchema.safeParse({ email: newsletterEmail.value })

  if (!result.success) {
    const firstErrorMessage =
      result.error.issues?.[0]?.message ||
      JSON.parse(result.error.message)[0]?.message ||
      t('errors.invalid.email')

    errorMessage.value = firstErrorMessage
    return
  }

  try {
    isLoading.value = true

    const { data: resData, errors } = await mutate<{
      subscribeNewsletter: { message: string }
    }>('SubscribeNewsletter', {
      email: newsletterEmail.value,
      gdpr: true,
      language: locale.value,
    })

    if (errors?.length) {
      errorMessage.value =
        errors[0].message || 'Došlo k chybě. Zkuste to prosím znovu.'
      return
    }

    if (resData?.subscribeNewsletter?.message) {
      successMessage.value = resData.subscribeNewsletter.message
    }

    timerId = setTimeout(() => {
      successMessage.value = ''
      newsletterEmail.value = ''
      errorMessage.value = ''
    }, 2000)
  } catch (error: any) {
    console.error('Newsletter error:', error)
    errorMessage.value = 'Došlo k neočekávané chybě. Zkuste to prosím znovu.'
  } finally {
    isLoading.value = false
  }
}

onUnmounted(() => {
  clearTimer()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
